/**
 * MissionMap_Entity type guard.
 *
 * @param {any} missionMapJson MissionMap object from API
 * @returns {boolean} Return true if type is MissionMap_Entity
 */
export default class MissionMap_Entity {
  constructor(missionMapJson: MissionMap_Entity) {
    try {
      this.ProjectName = missionMapJson.ProjectName;
      this.BookletToken = missionMapJson.BookletToken;
      this.SeasonName = missionMapJson.SeasonName;
      this.MusicianEmail = missionMapJson.MusicianEmail;
      this.EnableRelease = missionMapJson.EnableRelease;
      this.EventStatusTitle = missionMapJson.EventStatusTitle;
      this.ProjectLogo = missionMapJson.ProjectLogo;
      this.ContractorName = missionMapJson.ContractorName;
      this.CompanyIcon = missionMapJson.CompanyIcon;
      this.AssignmentID = missionMapJson.AssignmentID;
      this.Greetings = missionMapJson.Greetings;
      this.ContractorPhone = missionMapJson.ContractorPhone;
      this.CompanyName = missionMapJson.CompanyName;
      this.CoverURL = missionMapJson.CoverURL;
      this.ContractorEmail = missionMapJson.ContractorEmail;
      this.Memo = missionMapJson.Memo;
      this.MusicianName = missionMapJson.MusicianName;
      this.MusicianAvatar = missionMapJson.MusicianAvatar;
      this.Pieces = missionMapJson.Pieces;
      this.Interactors = missionMapJson.Interactors;
      this.CompanyColor = missionMapJson.CompanyColor;
      this.MessengerAvatar = missionMapJson.MessengerAvatar;
      this.MessengerName = missionMapJson.MessengerName;
      this.CompanyLogo = missionMapJson.CompanyLogo;
      this.ConductorName = missionMapJson.ConductorName;
      this.ScheduleDescription = missionMapJson.ScheduleDescription;
      this.Engagements = missionMapJson.Engagements;
      this.ShowFullSchedule = missionMapJson.ShowFullSchedule;
      this.IsCattleCall = missionMapJson.IsCattleCall;
      this.CattleCallSection = missionMapJson.CattleCallSection;
      this.TooLate = missionMapJson.TooLate;
      this.ReleaseExplanation = missionMapJson.ReleaseExplanation;
      this.ReleaseWorkSessionIDs = missionMapJson.ReleaseWorkSessionIDs;
      this.DressCode = missionMapJson.DressCode;
      this.FeaturedArtists = missionMapJson.FeaturedArtists;
      this.Links = missionMapJson.Links;
      this.ProjectRosters = missionMapJson.ProjectRosters;
      this.Wage = missionMapJson.Wage;
      this.WageUnit = missionMapJson.WageUnit;
      this.WageDescription = missionMapJson.WageDescription;
      this.WageBreakdown = missionMapJson.WageBreakdown;
      this.Contacts = missionMapJson.Contacts;
      this.OnboardingRequiredForContract =
        missionMapJson.OnboardingRequiredForContract;
      this.ProjectNotes = missionMapJson.ProjectNotes;
      this.PublicToken = missionMapJson.PublicToken;
      this.PayrollOnboarded = missionMapJson.PayrollOnboarded;
      this.Messages = missionMapJson.Messages;
      this.CurrentStage = missionMapJson.CurrentStage;
      this.Projects = missionMapJson.Projects;
      this.SectionName = missionMapJson.SectionName;
      this.RoleName = missionMapJson.RoleName;
      this.ProjectCount = missionMapJson.ProjectCount;
      this.ServiceCount = missionMapJson.ServiceCount;
      this.SeasonDateRange = missionMapJson.SeasonDateRange;
      this.DateRange = missionMapJson.DateRange;
      this.VenueSummary = missionMapJson.VenueSummary;
      this.PieceSummary = missionMapJson.PieceSummary;
      // Poll ('P' token): the standalone poll for this public token.
      this.Poll = missionMapJson.Poll;
      // Open polls for this project/season, answerable inline on the invite.
      this.Polls = missionMapJson.Polls;
      // ...and the same standalone poll spread flat — see pollViewFromFlatKeys.
      this.id = missionMapJson.id;
      this.title = missionMapJson.title;
      this.status = missionMapJson.status;
      this.resultsPublic = missionMapJson.resultsPublic;
      this.publicToken = missionMapJson.publicToken;
      this.questions = missionMapJson.questions;
      // Modern Calls power-up (org-gated server-side): absent for legacy
      // orgs, so every render below must treat undefined as the legacy look.
      this.ModernCall = missionMapJson.ModernCall;
      this.CallAccentColor = missionMapJson.CallAccentColor;
      this.CallAccentText = missionMapJson.CallAccentText;
      this.CallAccentIsLight = missionMapJson.CallAccentIsLight;
      this.CallFontStack = missionMapJson.CallFontStack;
      this.CallMastheadLogo = missionMapJson.CallMastheadLogo;
      this.CallNoteSections = missionMapJson.CallNoteSections;
      this.CallSectionOrder = missionMapJson.CallSectionOrder;
      this.GeneratedAt = missionMapJson.GeneratedAt;
    } catch (e) {
      throw new Error(
        `Failed to create new instance of ${MissionMap_Entity.name}`,
      );
    }
  }

  EventStatusTitle: string;
  OnboardingRequiredForContract?: boolean;
  CoverURL: string;
  EnableRelease: boolean;
  PublicToken: string;
  ProjectName: string;
  BookletToken?: string;
  PayrollOnboarded?: boolean;
  SeasonName: string;
  ProjectLogo?: string;
  ContractorName: string;
  Greetings?: string;
  ContractorPhone: string;
  CompanyName: string;
  CompanyIcon: string;
  ContractorEmail: string;
  Memo: string;
  DressCode?: string;
  FeaturedArtists?: string;
  MusicianName: string;
  MusicianEmail: string;
  Messages: Message[];
  MusicianAvatar?: string;
  AssignmentID?: number;
  Interactors: Interactor[];
  Pieces: Piece[];
  CompanyColor: string;
  MessengerAvatar: string;
  MessengerName: string;
  CompanyLogo: string;
  ReleaseWorkSessionIDs: string;
  ReleaseExplanation: string;
  ConductorName: string;
  ScheduleDescription: string;
  Projects: MissionMap_Entity[];
  /** Season digest tokens (rhapsody-service callSeason.go). Optional: a
   * mapping generated before they existed carries none, and the page
   * derives what it can from Engagements instead. */
  /** The musician's seat on this project (assignment mapping). */
  SectionName?: string;
  RoleName?: string;
  ProjectCount?: number;
  ServiceCount?: number;
  SeasonDateRange?: string;
  DateRange?: string;
  VenueSummary?: string;
  PieceSummary?: string;
  Engagements: { Type: string; Content: Engagement[] }[];
  ShowFullSchedule?: boolean;
  /** Modern Calls power-up. Absent (not false) for legacy orgs — the server
   * emits the key only when the org has it, so `=== true` is the gate. */
  ModernCall?: boolean;
  /** Org accent as picked; masthead rule only, never a page or fill color. */
  CallAccentColor?: string;
  /** Accent darkened server-side to read as text on white (WCAG AA). */
  CallAccentText?: string;
  CallAccentIsLight?: boolean;
  /** The org's typeface pick resolved to an email-safe stack server-side. */
  CallFontStack?: string;
  /** Masthead identity: the chosen branding asset's URL, or absent for the
   * wordmark. */
  CallMastheadLogo?: string;
  /** Org note sections: label = the note's name, body = sanitized rich text
   * retypeset by the renderer. The modern twin of the ProjectNotes blob. */
  CallNoteSections?: { Title: string; HTML: string }[];
  /** The org's section order (Branding page); ids from the shared list.
   * Absent = the built-in order. */
  CallSectionOrder?: string[];
  /** RFC3339 UTC stamp of when this mapping was built ("information as of"). */
  GeneratedAt?: string;
  IsCattleCall?: boolean;
  /** What a rush call is for: the section(s) of the seats on offer, or the
   * one the musician landed ("Cello", "Violin I & Violin II"). Absent on
   * mappings built before it existed and on regular calls. */
  CattleCallSection?: string;
  TooLate?: boolean;
  ProjectRosters: ProjectRoster[];
  Links: Link[];
  Wage?: string;
  WageUnit?: string;
  WageDescription?: string;
  /**
   * The computed pay breakdown — scale, overscale, adjustment, expenses, union
   * benefits — as HTML. Separate from WageDescription, which is whatever a person
   * wrote, so automatic wages add to the written terms instead of replacing them.
   */
  WageBreakdown?: string;
  Contacts: Contact[];
  ProjectNotes: string;
  CurrentStage?: {
    Color?: string;
    KeyName?: string;
    Terminus?: boolean;
    ID: number;
    Icon?: string;
    Name?: string;
  };

  // Poll ('P' token) — a poll rides the same public mission endpoints. `Poll`
  // is the standalone poll answered on the P-token page; `Polls` are the open
  // polls answerable inline on a project/season invite.
  Poll?: PollView;
  Polls?: PollView[];

  // A 'P' token's map carries that poll's PollView fields SPREAD at the top
  // level (lowercase), not nested under `Poll` — see pollViewFromFlatKeys.
  id?: number;
  title?: string;
  status?: string;
  resultsPublic?: boolean;
  publicToken?: string;
  questions?: PollQuestionView[];
}

// A poll option belongs to a QUESTION: an id + display label.
export type PollOptionView = {
  id: number;
  label: string;
};

// PollQuestionView is one question within a poll. `selectedOptionIDs`/`text`
// pre-fill the musician's previous answer; `results` is present only when the
// poll's results are public AND the musician answered (or the poll closed).
export type PollQuestionView = {
  questionID: number;
  prompt: string;
  kind: "choices" | "text" | "both";
  multiSelect?: boolean;
  minChoices?: number;
  maxChoices?: number;
  options?: PollOptionView[];
  selectedOptionIDs?: number[];
  text?: string;
  results?: { id: number; label: string; count: number }[];
};

// PollView is one poll rendered inline on a project/season invite or as the
// standalone poll page. It carries the poll's own token plus an ordered list of
// questions the musician answers together with a single submit.
export type PollView = {
  id?: number;
  publicToken: string;
  title?: string;
  status?: string; // draft | open | closed
  resultsPublic?: boolean;
  scope?: string;
  greeting?: string;
  questions?: PollQuestionView[];
};

/**
 * pollViewFromFlatKeys rebuilds the standalone poll from a 'P' token's mission
 * map. rhapsody-service spreads that poll's view fields at the TOP LEVEL of the
 * mapping (setPollMapping writes `id`/`title`/`status`/`resultsPublic`/
 * `publicToken`/`questions` lowercase) and only nests `Polls` for the A/M/G
 * invite tokens — so on the P page there is no `Poll` object to read.
 *
 * @param {MissionMap_Entity} mission The mission map for a 'P' token.
 * @returns {PollView | undefined} The poll, or undefined if the map carries none.
 */
export function pollViewFromFlatKeys(
  mission: MissionMap_Entity,
): PollView | undefined {
  const publicToken = mission.publicToken || mission.PublicToken;
  if (!publicToken || !mission.questions?.length) return undefined;
  return {
    id: mission.id,
    publicToken,
    title: mission.title,
    status: mission.status,
    resultsPublic: mission.resultsPublic,
    greeting: mission.Greetings,
    questions: mission.questions,
  };
}

/**
 * pollDisplayTitle resolves the label shown for a poll: its title, falling back
 * to the first question's prompt (matching the backend/email fallback).
 *
 * @param {PollView} poll The poll.
 * @returns {string} The display title.
 */
export function pollDisplayTitle(poll: PollView): string {
  return (poll.title ?? "").trim() || poll.questions?.[0]?.prompt || "Poll";
}

/**
 * pollIsAnswered reports whether the musician has answered the poll. There is no
 * poll-level answered flag in the contract; a poll is considered answered once
 * any question carries a pre-filled selection or text.
 *
 * @param {PollView} poll The poll.
 * @returns {boolean} True if any question has a pre-filled answer.
 */
export function pollIsAnswered(poll: PollView): boolean {
  return (poll.questions ?? []).some(
    (q) =>
      (q.selectedOptionIDs?.length ?? 0) > 0 ||
      (q.text ?? "").trim().length > 0,
  );
}

export type Message = {
  Color: string;
  ID: number;
  Icon: string;
  Name: string;
  Data: MissionMap_Entity;
  CreatedAt: string;
  CreatedBy: string;
  Inspectors: {
    ID: number;
    Title: string;
    Message?: any;
    Icon?: string;
    Kind?: string;
    CreatedAt?: string;
    CreatedBy?: string;
  }[];
  Interactors: Interactor[];
  NudgeCount: number;
  Acknowledged: boolean;
  JobStageID?: number;
};

export type Contact = {
  Name: string;
  Role: string;
  Email?: string;
  Phone?: string;
  Description?: string;
  Avatar?: string;
};

export type Piece = {
  Composer: string;
  Title: string;
  Description: string;
  Playing: boolean;
  SectionName?: string;
  SectionRole?: string;
  SectionOrder?: number;
  Instruments?: string[];
  ProjectPieceID?: number;
  Memo?: string;
  // The movements the project plays of this work, pooled across its sessions.
  // Absent means the whole work, which the title already says.
  Movements?: string[];
  MovementsLabel?: string; // joined/compacted form for inline rendering
  MovementsCompact?: boolean; // true when MovementsLabel gave up on titles and rendered a range
};

type Link = {
  Name: string;
  Description: string;
  URL: string;
};

export type ProjectRoster = {
  Name: string;
  URL: string;
};

export type Inspector = {
  id: number;
  title: string;
  message?: any;
  icon?: string;
  kind?: string;
};

export type Interactor = {
  ID: number;
  Name: string;
  Color?: string;
  /** Label color derived server-side from Color (WCAG). Only present on v3
   * mission payloads — the v2 assignment path rebuilds interactors through a
   * struct and drops it, so always fall back to computing it locally. */
  TextColor?: string;
  KeyName?: string;
  Config?: string;
  Kind?: string;
  Icon?: string;
  Description?: string;
  Timezone?: string;
  Position?: number;
  SegueID?: number;
  StageID?: number;
};

export type Engagement = {
  ID: number;
  WorkSessionID: number;
  Title: string;
  Memo?: string;
  /** The work session's conductor, mapped since the Modern Calls backend. */
  Conductor?: string;
  VenueName: string;
  /** The venue's abbreviation ("GMC"), used on session rows once the schedule's
   * Locations list introduced the full venue. Absent on mappings stored before
   * venue short names existed. */
  VenueShortName?: string;
  DateFromUTC: string;
  Timezone: string;
  DateToUTC: string;
  WeekDay: string;
  Day: string;
  Month: string;
  HourRange: string;
  VenueAddress: string;
  VenueLat?: string;
  VenueLng?: string;
  VenueTzName: string;
  Playing: boolean;
  Plays: {
    PieceNumber: number;
    Playing: boolean;
    Composer: string;
    ProjectPieceID: number;
    Title: string;
    // ProgramSlot fields (#294): a slot is a whole piece, a standalone movement, or a break.
    Kind?: string; // "piece" | "movement" | "break" | "unpaidBreak"
    Duration?: number | null; // minutes, when set on the slot
    MovementIDs?: string;
    Movements?: string[]; // resolved standalone-movement labels, e.g. ["I. Allegro con brio"]
    MovementsLabel?: string; // joined form of Movements for inline rendering
    // MovementsLabel minus the repetition: empty when this session plays exactly
    // what the project plays of the work, since the repertoire section carries
    // that list. A session playing a subset still spells it out.
    SessionMovementsLabel?: string;
    /** When the slot starts, pre-formatted in the venue's clock ("7:30PM"),
     * computed upstream from the session start and the slot durations —
     * starred ("7:30PM*") when the project marks its times approximate, cueing
     * the "* Times are estimates." footnote. Absent while the running time is
     * unknown (a prior slot without a duration) or when the schedule carries
     * no durations at all (order-only, Monterey style). */
    StartLabel?: string;
  }[];
  Chairs?: [
    {
      ID?: number;
      // The chair's piece position — 0 on a v3 project, whose seating is work-session anchored
      // rather than per piece.
      PieceNumber: number;
      Memo: string;
      SectionRole?: string;
      SectionName?: string;
      SectionOrder?: string;
      Instruments: string[];
    },
  ];
};
